import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import SchnellAktionen from '../../../components/testing/SchnellAktionen';
import { SpaltenKopf, type FilterWert } from '../../../components/testing/SpaltenKopf';
import {
  testingApi,
  TICKET_KIND_LABEL,
  TICKET_SEVERITY_LABEL,
  TICKET_STATUS_LABEL,
  type TicketAssigneeDto,
  type TicketCategoryDto,
  type TicketDto,
  type TicketKind,
  type TicketSeverity,
  type TicketSort,
  type TicketStatsDto,
  type TicketStatus,
} from '../../../lib/api/testing';

const SEITENGROESSEN = [25, 50, 100];

/** Wonach sich gruppieren lässt. „keine" zeigt die flache Liste mit Seiten. */
const GRUPPEN = {
  keine: 'keine Gruppierung',
  status: 'Status',
  severity: 'Schweregrad',
  area: 'Kategorie',
  kind: 'Art',
} as const;
type GruppenKey = keyof typeof GRUPPEN;

/**
 * Beim Gruppieren gibt es keine Seiten — sonst stünde eine Gruppe halb auf
 * Seite 1 und halb auf Seite 2. Stattdessen wird bis zu dieser Zahl alles
 * geholt; darüber sagt die Ansicht, dass sie abschneidet.
 */
const GRUPPEN_GRENZE = 500;

/**
 * Die eingestellte Ansicht überlebt das Neuladen.
 *
 * Bewusst nicht über `useStandortState`: Der Speicher dort hängt am gewählten
 * Standort, Tickets sind aber standortübergreifend — die Einstellung wäre nach
 * jedem Standortwechsel weg. Stattdessen ein eigener Schlüssel, wie ihn die
 * Sidebar für ihren Zustand auch benutzt.
 *
 * Nicht gespeichert wird der Suchbegriff: Ein Suchtext, der einen Tag später
 * still die halbe Liste ausblendet, kostet mehr Zeit als er spart.
 */
const ANSICHT_KEY = 'sidigo-testing-ansicht';

/** Nach Zuständigkeit filtern. Die Schlüssel kennt der Server so. */
const ZUSTAENDIGKEIT = {
  alle: 'Alle Tickets',
  mir: 'Meine Tickets',
  ohne: 'Ohne Zuständigkeit',
} as const;
type ZustaendigkeitKey = keyof typeof ZUSTAENDIGKEIT;

interface GespeicherteAnsicht {
  status?: string[];
  schwere?: string[];
  arten?: string[];
  bereiche?: string[];
  nurOffene?: boolean;
  /** Alter Schalter; wird beim Laden in `zustaendigkeit` überführt. */
  nurMeine?: boolean;
  zustaendigkeit?: ZustaendigkeitKey;
  gruppierung?: GruppenKey;
  seitengroesse?: number;
  sortierung?: { key: TicketSort; richtung: 'asc' | 'desc' };
  zugeklappt?: string[];
}

function ladeAnsicht(): GespeicherteAnsicht {
  try {
    const roh = localStorage.getItem(ANSICHT_KEY);
    return roh ? (JSON.parse(roh) as GespeicherteAnsicht) : {};
  } catch {
    // Privater Modus, gesperrte Site-Daten, kaputter Eintrag: dann eben die Vorbelegung.
    return {};
  }
}

function merkeAnsicht(ansicht: GespeicherteAnsicht): void {
  try {
    localStorage.setItem(ANSICHT_KEY, JSON.stringify(ansicht));
  } catch {
    // Speichern ist Komfort; die Liste funktioniert auch ohne.
  }
}

const severityVariant = (schwere: TicketSeverity): 'destructive' | 'default' | 'secondary' =>
  schwere === 'blocker' ? 'destructive' : schwere === 'hoch' ? 'default' : 'secondary';

const alter = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return '—';
  const stunden = Math.floor((Date.now() - datum.getTime()) / 3_600_000);
  if (stunden < 1) return 'gerade eben';
  if (stunden < 24) return `vor ${stunden} h`;
  return `vor ${Math.floor(stunden / 24)} T`;
};

/**
 * Ticketliste.
 *
 * Sortiert, gefiltert und geblättert wird auf dem Server — bei 38 Tickets wäre
 * das noch egal, bei 300 nicht mehr. Gruppiert wird im Browser, weil dafür
 * ohnehin alle Zeilen der Auswahl gebraucht werden.
 */
export default function TicketsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<TicketDto[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [facets, setFacets] = useState<Record<string, Record<string, number>>>({});
  const [kategorien, setKategorien] = useState<TicketCategoryDto[]>([]);
  const [zahlen, setZahlen] = useState<TicketStatsDto | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [nichtFreigeschaltet, setNichtFreigeschaltet] = useState(false);

  // Einmal beim Aufbauen gelesen; danach schreibt der Effekt unten zurück.
  const [gemerkt] = useState<GespeicherteAnsicht>(ladeAnsicht);

  const [status, setStatus] = useState<string[]>(gemerkt.status ?? []);
  const [schwere, setSchwere] = useState<string[]>(gemerkt.schwere ?? []);
  const [arten, setArten] = useState<string[]>(gemerkt.arten ?? []);
  const [bereiche, setBereiche] = useState<string[]>(gemerkt.bereiche ?? []);
  const [suche, setSuche] = useState('');
  // Vorbelegt: was noch zu tun ist, nach Schweregrad gruppiert. Die
  // Arbeitsreihenfolge soll beim Öffnen dastehen, nicht das Archiv.
  const [nurOffene, setNurOffene] = useState(gemerkt.nurOffene ?? true);
  // Der alte Schalter lebt als Vorbelegung weiter, damit eine gemerkte Ansicht
  // beim Umstieg nicht verloren geht.
  const [zustaendigkeit, setZustaendigkeit] = useState<ZustaendigkeitKey>(
    gemerkt.zustaendigkeit ?? (gemerkt.nurMeine ? 'mir' : 'alle')
  );

  const [sortierung, setSortierung] = useState<{ key: TicketSort; richtung: 'asc' | 'desc' }>(
    gemerkt.sortierung ?? { key: 'severity', richtung: 'desc' },
  );
  const [seite, setSeite] = useState(0);
  const [seitengroesse, setSeitengroesse] = useState(gemerkt.seitengroesse ?? 25);
  const [gruppierung, setGruppierung] = useState<GruppenKey>(gemerkt.gruppierung ?? 'severity');
  const [zugeklappt, setZugeklappt] = useState<string[]>(gemerkt.zugeklappt ?? []);

  const gruppiert = gruppierung !== 'keine';

  // Wer ich bin, sagt die Zuständigen-Liste — nur damit „Mir zuweisen" bei
  // meinen eigenen Tickets wegbleibt. Aufgelöst wird die Zuweisung ohnehin auf
  // dem Server, der Browser rät seine Konto-ID nicht.
  const [ich, setIch] = useState<TicketAssigneeDto | null>(null);

  useEffect(() => {
    testingApi
      .status()
      .then((daten) => setKategorien(daten?.categories ?? []))
      .catch(() => setKategorien([]));
  }, []);

  useEffect(() => {
    let abgebrochen = false;
    testingApi
      .assignees()
      .then((antwort) => {
        if (!abgebrochen) setIch(antwort.items.find((person) => person.isMe) ?? null);
      })
      // Ohne das Recht gibt es keine Liste; dann bleibt der Eintrag sichtbar und
      // der Server weist ihn ab — das ist ehrlicher als ihn stumm zu verstecken.
      .catch(() => undefined);
    return () => {
      abgebrochen = true;
    };
  }, []);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const [liste, kennzahlen] = await Promise.all([
        testingApi.list({
          status: status as TicketStatus[],
          severity: schwere as TicketSeverity[],
          kind: arten as TicketKind[],
          area: bereiche,
          q: suche || undefined,
          assigned: zustaendigkeit === 'alle' ? undefined : zustaendigkeit,
          includeClosed: !nurOffene,
          sort: sortierung.key,
          direction: sortierung.richtung,
          limit: gruppiert ? GRUPPEN_GRENZE : seitengroesse,
          offset: gruppiert ? 0 : seite * seitengroesse,
        }),
        testingApi.stats().catch(() => null),
      ]);
      setItems(Array.isArray(liste?.items) ? liste.items : []);
      setGesamt(liste?.total ?? 0);
      setFacets(liste?.facets ?? {});
      setZahlen(kennzahlen);
      setNichtFreigeschaltet(false);
    } catch (fehler) {
      const meldung = fehler instanceof Error ? fehler.message : '';
      if (meldung.toLowerCase().includes('nicht freigeschaltet')) {
        setNichtFreigeschaltet(true);
      } else {
        toast.error(meldung || 'Die Tickets konnten nicht geladen werden.');
      }
      setItems([]);
    } finally {
      setLaedt(false);
    }
  }, [
    status,
    schwere,
    arten,
    bereiche,
    suche,
    nurOffene,
    zustaendigkeit,
    sortierung,
    seite,
    seitengroesse,
    gruppiert,
  ]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Jede Änderung an der Ansicht wandert in den Speicher — auch welche Gruppen
  // zugeklappt sind, sonst klappt man dieselben nach jedem Neuladen wieder zu.
  useEffect(() => {
    merkeAnsicht({
      status,
      schwere,
      arten,
      bereiche,
      nurOffene,
      zustaendigkeit,
      gruppierung,
      seitengroesse,
      sortierung,
      zugeklappt,
    });
  }, [
    status,
    schwere,
    arten,
    bereiche,
    nurOffene,
    zustaendigkeit,
    gruppierung,
    seitengroesse,
    sortierung,
    zugeklappt,
  ]);

  // Jeder Filterwechsel fängt wieder auf Seite 1 an — sonst steht man auf einer
  // Seite, die es in der neuen Auswahl gar nicht mehr gibt.
  useEffect(() => {
    setSeite(0);
  }, [status, schwere, arten, bereiche, suche, nurOffene, zustaendigkeit, seitengroesse]);

  const sortieren = (key: string) => {
    setSortierung((vorher) =>
      vorher.key === key
        ? { key: vorher.key, richtung: vorher.richtung === 'asc' ? 'desc' : 'asc' }
        : { key: key as TicketSort, richtung: 'desc' },
    );
  };

  const kategorieLabel = useCallback(
    (wert: string) =>
      wert === 'ohne'
        ? 'noch nicht eingeordnet'
        : (kategorien.find((eintrag) => eintrag.key === wert)?.label ?? wert),
    [kategorien],
  );

  const auswahlAus = (
    dimension: string,
    beschriftung: (wert: string) => string,
    reihenfolge?: readonly string[],
  ): FilterWert[] => {
    const werte = facets[dimension] ?? {};
    const schluessel = reihenfolge
      ? reihenfolge.filter((wert) => wert in werte)
      : Object.keys(werte).sort();
    return schluessel.map((wert) => ({ wert, label: beschriftung(wert), anzahl: werte[wert] }));
  };

  const gruppen = useMemo(() => {
    if (!gruppiert) return [];
    const beschriftung = (ticket: TicketDto): string => {
      if (gruppierung === 'status') return TICKET_STATUS_LABEL[ticket.status] ?? ticket.status;
      if (gruppierung === 'severity') return TICKET_SEVERITY_LABEL[ticket.severity];
      if (gruppierung === 'kind') return TICKET_KIND_LABEL[ticket.kind];
      return kategorieLabel(ticket.area ?? 'ohne');
    };
    const karte = new Map<string, TicketDto[]>();
    for (const ticket of items) {
      const name = beschriftung(ticket);
      karte.set(name, [...(karte.get(name) ?? []), ticket]);
    }
    return [...karte.entries()];
  }, [gruppiert, gruppierung, items, kategorieLabel]);

  const filterAktiv =
    status.length > 0 || schwere.length > 0 || arten.length > 0 || bereiche.length > 0 || !!suche;

  if (nichtFreigeschaltet) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          Das Testing-Modul ist auf diesem System nicht freigeschaltet
          (<code>TESTING_ENABLED</code>).
        </div>
      </div>
    );
  }

  const zeile = (ticket: TicketDto) => (
    <TableRow
      key={ticket.id}
      className="cursor-pointer"
      onClick={() => navigate(`/testing/${ticket.id}`)}
    >
      <TableCell className="font-mono text-xs">{ticket.label}</TableCell>
      <TableCell>
        <div className="font-medium">{ticket.title}</div>
        {ticket.route && <div className="text-xs text-muted-foreground">{ticket.route}</div>}
      </TableCell>
      <TableCell className="text-sm">
        {ticket.area ? (
          kategorieLabel(ticket.area)
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={severityVariant(ticket.severity)}>
          {TICKET_SEVERITY_LABEL[ticket.severity]}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{TICKET_STATUS_LABEL[ticket.status]}</TableCell>
      <TableCell className="text-sm">{ticket.reporterName ?? '—'}</TableCell>
      <TableCell className="text-sm">
        {ticket.assignedName ?? <span className="text-muted-foreground">niemand</span>}
      </TableCell>
      <TableCell className="text-sm">
        {ticket.lastAction ? (
          <>
            <div className="line-clamp-2">{ticket.lastAction.description}</div>
            <div className="text-xs text-muted-foreground">
              {ticket.lastAction.actorName ?? 'System'} · {alter(ticket.lastAction.createdAt)}
            </div>
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{alter(ticket.createdAt)}</TableCell>
      <TableCell className="w-10 text-right">
        <SchnellAktionen
          ticket={ticket}
          gehoertMir={!!ich && ticket.assignedAccountId === ich.accountId}
          onGeaendert={() => void laden()}
        />
      </TableCell>
    </TableRow>
  );

  const kopf = (
    <TableHeader>
      <TableRow>
        <TableHead className="w-24">
          <SpaltenKopf
            titel="Nummer"
            sortKey="number"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
          />
        </TableHead>
        <TableHead>
          <SpaltenKopf
            titel="Titel"
            sortKey="title"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
          />
        </TableHead>
        <TableHead className="w-44">
          <SpaltenKopf
            titel="Kategorie"
            sortKey="area"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
            werte={auswahlAus('area', kategorieLabel)}
            gewaehlt={bereiche}
            onFiltern={setBereiche}
          />
        </TableHead>
        <TableHead className="w-28">
          <SpaltenKopf
            titel="Schwere"
            sortKey="severity"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
            werte={auswahlAus(
              'severity',
              (wert) => TICKET_SEVERITY_LABEL[wert as TicketSeverity] ?? wert,
              ['blocker', 'hoch', 'mittel', 'niedrig'],
            )}
            gewaehlt={schwere}
            onFiltern={setSchwere}
          />
        </TableHead>
        <TableHead className="w-40">
          <SpaltenKopf
            titel="Status"
            sortKey="status"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
            werte={auswahlAus(
              'status',
              (wert) => TICKET_STATUS_LABEL[wert as TicketStatus] ?? wert,
              Object.keys(TICKET_STATUS_LABEL),
            )}
            gewaehlt={status}
            onFiltern={setStatus}
          />
        </TableHead>
        <TableHead className="w-36">
          <SpaltenKopf
            titel="Melder"
            sortKey="reporter"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
            werte={auswahlAus(
              'kind',
              (wert) => TICKET_KIND_LABEL[wert as TicketKind] ?? wert,
              Object.keys(TICKET_KIND_LABEL),
            )}
            gewaehlt={arten}
            onFiltern={setArten}
          />
        </TableHead>
        <TableHead className="w-36">
          <SpaltenKopf
            titel="Zuständig"
            sortKey="assignee"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
          />
        </TableHead>
        <TableHead>
          <SpaltenKopf titel="Letzte Aktion" />
        </TableHead>
        <TableHead className="w-24">
          <SpaltenKopf
            titel="Alter"
            sortKey="created"
            aktiveSortierung={sortierung}
            onSortieren={sortieren}
          />
        </TableHead>
        <TableHead className="w-10">
          <span className="sr-only">Aktionen</span>
        </TableHead>
      </TableRow>
    </TableHeader>
  );

  const letzteSeite = Math.max(0, Math.ceil(gesamt / seitengroesse) - 1);
  const von = gesamt === 0 ? 0 : seite * seitengroesse + 1;
  const bis = Math.min(gesamt, (seite + 1) * seitengroesse);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Tickets</h1>
          <p className="text-muted-foreground">
            Meldungen aus der Testphase — mit dem Zustand, in dem sie entstanden sind.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {zahlen && (
            <>
              <Badge variant="secondary">{zahlen.open} offen</Badge>
              {zahlen.blocker > 0 && <Badge variant="destructive">{zahlen.blocker} Blocker</Badge>}
              {/* Der Weg in den Backlog. Zurückgestelltes steht nicht von allein
                  in der Liste — sonst wäre es kein Backlog. Die Zahl ist der
                  Hinweis, dass dort etwas liegt, und der Klick der Weg hin. */}
              {(zahlen.backlog ?? 0) > 0 && (
                <Badge
                  variant="outline"
                  className="cursor-pointer hover:bg-accent"
                  role="button"
                  tabIndex={0}
                  onClick={() => setStatus(['backlog'])}
                  onKeyDown={(ereignis) => {
                    if (ereignis.key === 'Enter' || ereignis.key === ' ') setStatus(['backlog']);
                  }}
                  title="Zurückgestellte Tickets anzeigen"
                >
                  {zahlen.backlog} im Backlog
                </Badge>
              )}
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => void laden()} disabled={laedt}>
            {laedt ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Aktualisieren
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <div className="min-w-[16rem] flex-1 space-y-2">
            <Label htmlFor="ticket-suche">Suche</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="ticket-suche"
                value={suche}
                onChange={(event) => setSuche(event.target.value)}
                placeholder="Titel, Text oder Seite"
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Gruppierung</Label>
            <Select value={gruppierung} onValueChange={(wert) => setGruppierung(wert as GruppenKey)}>
              <SelectTrigger className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(GRUPPEN).map(([wert, label]) => (
                  <SelectItem key={wert} value={wert}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant={nurOffene ? 'default' : 'outline'}
            onClick={() => setNurOffene((vorher) => !vorher)}
          >
            Nur offene
          </Button>
          <div className="space-y-2">
            <Label>Zuständigkeit</Label>
            <Select
              value={zustaendigkeit}
              onValueChange={(wert) => setZustaendigkeit(wert as ZustaendigkeitKey)}
            >
              <SelectTrigger className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ZUSTAENDIGKEIT).map(([wert, label]) => (
                  <SelectItem key={wert} value={wert}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {filterAktiv && (
            <Button
              variant="ghost"
              onClick={() => {
                setStatus([]);
                setSchwere([]);
                setArten([]);
                setBereiche([]);
                setSuche('');
              }}
            >
              <X className="mr-2 h-4 w-4" />
              Filter zurücksetzen
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            {gruppiert
              ? `${items.length} Tickets in ${gruppen.length} Gruppen`
              : `${von}–${bis} von ${gesamt}`}
          </CardTitle>
          {!gruppiert && (
            <div className="flex items-center gap-2">
              <Select
                value={String(seitengroesse)}
                onValueChange={(wert) => setSeitengroesse(Number(wert))}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEITENGROESSEN.map((groesse) => (
                    <SelectItem key={groesse} value={String(groesse)}>
                      {groesse}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={seite <= 0 || laedt}
                onClick={() => setSeite((vorher) => Math.max(0, vorher - 1))}
              >
                Zurück
              </Button>
              <span className="text-sm text-muted-foreground">
                {seite + 1} / {letzteSeite + 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={seite >= letzteSeite || laedt}
                onClick={() => setSeite((vorher) => Math.min(letzteSeite, vorher + 1))}
              >
                Weiter
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              {laedt ? 'Wird geladen …' : 'Keine Tickets — mit diesem Filter ist nichts da.'}
            </div>
          ) : gruppiert ? (
            <div className="space-y-4">
              {gesamt > GRUPPEN_GRENZE && (
                <p className="text-sm text-muted-foreground">
                  Beim Gruppieren zeigt die Ansicht die ersten {GRUPPEN_GRENZE} von {gesamt}{' '}
                  Tickets. Filtere enger, um alles zu sehen.
                </p>
              )}
              {gruppen.map(([name, zeilen]) => {
                const offen = !zugeklappt.includes(name);
                return (
                  <div key={name} className="rounded-lg border">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/50"
                      onClick={() =>
                        setZugeklappt((vorher) =>
                          vorher.includes(name)
                            ? vorher.filter((eintrag) => eintrag !== name)
                            : [...vorher, name],
                        )
                      }
                    >
                      {offen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="font-medium">{name}</span>
                      <Badge variant="secondary">{zeilen.length}</Badge>
                    </button>
                    {offen && (
                      <Table>
                        {kopf}
                        <TableBody>{zeilen.map(zeile)}</TableBody>
                      </Table>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Table>
              {kopf}
              <TableBody>{items.map(zeile)}</TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
